// /src/pages/EditLabelledStock.jsx
import React, { useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";

// masters
import { useGetAllVendorsQuery } from "../toolkitServices/vendorApi";
import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { useGetAllCategoriesQuery } from "../toolkitServices/categoryApi";
import { useGetAllProductsQuery } from "../toolkitServices/productApi";
import { useGetAllDesignsQuery } from "../toolkitServices/designApi";
import { useGetAllPuritiesQuery } from "../toolkitServices/purityApi";
import { useGetAllStonesQuery } from "../toolkitServices/stoneApi";
import { useGetAllDiamondsQuery } from "../toolkitServices/diamondApi";
import { useGetCountersQuery } from "../toolkitServices/counterApi";

// store fill
import { setCategoryList } from "../slices/categorySlice";
import { setProductList } from "../slices/productSlice";
import { setVendorList } from "../slices/vendorSlice";
import { setDesignList } from "../slices/designSlice";
import { setPurityList } from "../slices/puritySlice";
import { setBranchList } from "../slices/branchSlice";
import { setStoneList } from "../slices/stoneSlice";
import { setDiamondList } from "../slices/diamondSlice";
import { setCounterList } from "../slices/counterSlice";

// item api
import { useGetLabelledItemByIdQuery } from "../toolkitServices/stockApi";

// form
import LabelledStockForm from "../components/LabelledStockForm";

/* ------------------ helpers ------------------ */
const toOpt = (id, label) =>
  id ? { value: String(id), label: label || "" } : null;

const mapItemToForm = (doc) => {
  if (!doc) return null;
  return {
    vendor: toOpt(doc.vendorId, doc.vendorName),
    branch: toOpt(doc.branchId, doc.branchName),
    counter: toOpt(doc.counterId, doc.counterName),
    stockEntries: [
      {
        totalWt: doc.totalWt ?? "",
        packingWt: doc.packingWt ?? "",
        grossWt: doc.grossWt ?? "",
        stoneWt: doc.stoneWt ?? "",
        diamondWt: doc.diamondWt ?? "", // grams stored at item level
        netWt: doc.netWt ?? "",

        qty: doc.qty ?? "",
        pcs: doc.pcs ?? "",
        stonePcs: doc.stonePcs ?? "",
        diamondPcs: doc.diamondPcs ?? "",

        makingPercentage: doc.makingPercentage ?? "",
        makingPerGram: doc.makingPerGram ?? "",
        fixedMaking: doc.fixedMaking ?? "",
        fixedAmount: doc.fixedAmount ?? "",

        totalStoneAmount: doc.totalStoneAmount ?? "",
        totalDiamondAmount: doc.totalDiamondAmount ?? "",
        hallmarkCharge: doc.hallmarkCharge ?? "",

        mrp: doc.mrp ?? "",
        sampleName: doc.sampleName ?? "",
        huidCode: doc.huidCode ?? "",
        productCode: doc.productCode ?? "",
        rfidCode: doc.rfidCode ?? "",
        description: doc.description ?? "",

        category: toOpt(doc.categoryId, doc.categoryName),
        product: toOpt(doc.productId, doc.productName),
        design: toOpt(doc.designId, doc.designName),
        purity: toOpt(doc.purityId, doc.purityName),

        // DB-shape stones/diamonds — form me popup-shape me normalize hoga
        stones: Array.isArray(doc.stones) ? doc.stones : [],
        diamonds: Array.isArray(doc.diamonds) ? doc.diamonds : [],
         images: Array.isArray(doc.images)
      ? doc.images.map((img) => ({
          id: img.publicId || img.url,
          url: img.url,
          previewUrl: img.url,   // 👈 VERY IMPORTANT
          name: img.originalName,
          size: img.size,
          _fromInitial: true,
        }))
      : [],
      },
    ],
  };
};

const EditLabelledStock = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // masters
  const { data: vendors }    = useGetAllVendorsQuery();
  const { data: branches }   = useGetAllBranchesQuery();
  const { data: categories } = useGetAllCategoriesQuery();
  const { data: products }   = useGetAllProductsQuery();
  const { data: designs }    = useGetAllDesignsQuery();
  const { data: purities }   = useGetAllPuritiesQuery();
  const { data: stones }     = useGetAllStonesQuery();
  const { data: diamonds }   = useGetAllDiamondsQuery();
  const { data: counters }   = useGetCountersQuery();

  // single item
  const { data: itemRes, isFetching, isError } = useGetLabelledItemByIdQuery(id);

  // push masters in store
  useEffect(() => {
    if (categories?.data?.length) dispatch(setCategoryList(categories.data));
    if (products?.data?.length)   dispatch(setProductList(products.data));
    if (vendors?.data?.length)    dispatch(setVendorList(vendors.data));
    if (designs?.data?.length)    dispatch(setDesignList(designs.data));
    if (purities?.data?.length)   dispatch(setPurityList(purities.data));
    if (branches?.data?.length)   dispatch(setBranchList(branches.data));
    if (stones?.data?.length)     dispatch(setStoneList(stones.data));
    if (diamonds?.data?.length)   dispatch(setDiamondList(diamonds.data));
    if (counters?.data?.length)   dispatch(setCounterList(counters.data));
  }, [
    dispatch,
    categories,
    products,
    vendors,
    designs,
    purities,
    branches,
    stones,
    diamonds,
    counters,
  ]);


   useEffect(() => {
      const handleKeyDown = (e) => {
        if (e.ctrlKey && e.key === "Backspace") {
          e.preventDefault();
          navigate(-1);
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, [navigate]);

    
  useEffect(() => {
    if (isError) toast.error("Item not found");
  }, [isError]);

  const existingData = useMemo(
    () => (itemRes?.data ? mapItemToForm(itemRes.data) : null),
    [itemRes]
  );

  
   
  if (isFetching || !existingData) {
    return (
      <div className="bg-white rounded-xl shadow p-6 text-sm text-slate-600">
        Loading item…
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="max-w-8xl mx-auto bg-white shadow-lg rounded-xl px-2 py-2"
    >
      <LabelledStockForm
        mode="edit"
        itemId={id}
        existingData={existingData}
        allowMultipleRows={false}
      />
    </motion.div>
  );
};

export default EditLabelledStock;
